import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  CalendarDays,
  ArrowRight,
  TrendingUp,
  Clock,
  Bell,
  AlertCircle,
  StickyNote,
  Plus,
  Pin,
  Trash2,
  Pencil,
  X,
  Loader2,
  Scale,
  Thermometer,
  ClipboardCheck,
  Upload,
  FileText,
  Send,
  Calendar,
  RefreshCw,
  HelpCircle
} from "lucide-react";
import { Layout } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { OnboardingTour, useOnboarding, dashboardTourSteps } from "../components/OnboardingTour";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { apiRequest, formatDate, getTipoEventoLabel, getDaysRemaining, API_URL, hojeBrasil, horaBrasil } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ToastProvider";
import { MarcarEnvioDialog } from "../components/MarcarEnvioDialog";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";

const StatCard = ({ title, value, icon: Icon, color, description }) => {
  const colorClasses = {
    emerald: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800",
    amber: "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-800",
    red: "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-100 dark:border-red-800",
    teal: "bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border-teal-100 dark:border-teal-800",
    orange: "bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-800",
    purple: "bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-800",
  };

  return (
    <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 dark:bg-slate-800/50 dark:border-slate-700" data-testid={`stat-card-${color}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{title}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1" style={{ fontFamily: 'Manrope' }}>
              {value}
            </p>
            {description && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl border ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AlertBadge = ({ nivel, dias }) => {
  const config = {
    vencido: { bg: "bg-red-100", text: "text-red-700", label: "VENCIDO" },
    critico: { bg: "bg-red-100", text: "text-red-700", label: `${dias} dias` },
    alto: { bg: "bg-orange-100", text: "text-orange-700", label: `${dias} dias` },
    medio: { bg: "bg-amber-100", text: "text-amber-700", label: `${dias} dias` },
  };
  
  const c = config[nivel] || { bg: "bg-slate-100", text: "text-slate-700", label: `${dias} dias` };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

const UpcomingEventRow = ({ evento }) => {
  const dias = evento.dias_restantes ?? getDaysRemaining(evento.data_vencimento);
  const nivel = evento.nivel_alerta;
  
  return (
    <div className={`flex items-center justify-between py-3 px-3 rounded-lg mb-2 ${
      nivel === "vencido" ? "bg-red-50 border border-red-200" :
      nivel === "critico" ? "bg-red-50/50 border border-red-100" :
      nivel === "alto" ? "bg-orange-50/50 border border-orange-100" :
      nivel === "medio" ? "bg-amber-50/50 border border-amber-100" :
      "bg-slate-50"
    }`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{evento.nome}</p>
        <p className="text-sm text-slate-500">{getTipoEventoLabel(evento.tipo)}</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className="text-sm font-medium text-slate-900">{formatDate(evento.data_vencimento)}</p>
        </div>
        <AlertBadge nivel={nivel} dias={dias} />
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Onboarding Tour
  const { runTour, setRunTour, tourCompleted, startTour, completeTour, resetTour } = useOnboarding();
  
  // Verificações de itens pendentes
  const [verificacoesPendentes, setVerificacoesPendentes] = useState([]);
  const [verificandoItem, setVerificandoItem] = useState(null);
  const [itemParaVerificar, setItemParaVerificar] = useState(null);
  
  // Envios mensais pendentes
  const [enviosPendentes, setEnviosPendentes] = useState([]);
  
  // Todos os envios (para aba de envios)
  const [todosEnvios, setTodosEnvios] = useState([]);
  const [loadingEnvios, setLoadingEnvios] = useState(false);
  const [marcandoEnviado, setMarcandoEnviado] = useState(null);
  
  // Diálogo de marcar envio
  const [marcarEnvioDialogOpen, setMarcarEnvioDialogOpen] = useState(false);
  const [envioSelecionado, setEnvioSelecionado] = useState(null);
  
  // Rotinas personalizadas pendentes
  const [rotinasPendentes, setRotinasPendentes] = useState([]);
  const [registrandoRotina, setRegistrandoRotina] = useState(null);
  
  // Diálogo para registrar rotina personalizada
  const [showRotinaDialog, setShowRotinaDialog] = useState(false);
  const [rotinaSelecionada, setRotinaSelecionada] = useState(null);
  const [rotinaFormData, setRotinaFormData] = useState({
    conforme: true,
    responsavel: "",
    observacoes: "",
    campos_valores: {}
  });
  const [tiposVerificacao, setTiposVerificacao] = useState([]);
  
  // Exclusão de notas com confirmação de senha
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [notaToDelete, setNotaToDelete] = useState(null);
  
  // Modais para cada tipo de verificação
  const [balancaModalOpen, setBalancaModalOpen] = useState(false);
  const [termoModalOpen, setTermoModalOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  
  // Formulários específicos
  const [balancaForm, setBalancaForm] = useState({
    data_verificacao: hojeBrasil(),
    hora_verificacao: horaBrasil(),
    peso_padrao: "",
    peso_indicado: "",
    diferenca: "",
    conforme: true,
    responsavel: "",
    observacoes: "",
  });
  
  const [termoForm, setTermoForm] = useState({
    data_verificacao: hojeBrasil(),
    hora_verificacao: horaBrasil(),
    temperatura_lida: "",
    umidade_lida: "",
    conforme: true,
    responsavel: "",
    observacoes: "",
  });
  
  const [customForm, setCustomForm] = useState({
    data_verificacao: hojeBrasil(),
    hora_verificacao: horaBrasil(),
    conforme: true,
    responsavel: "",
    observacoes: "",
    arquivo_url: "",
    arquivo_nome: "",
  });
  
  // Upload de arquivo para custom
  const [customUploading, setCustomUploading] = useState(false);
  const customFileInputRef = useRef(null);
  
  const [saving, setSaving] = useState(false);
  
  // Estado para notas
  const [notas, setNotas] = useState([]);
  const [notaDialogOpen, setNotaDialogOpen] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [notaForm, setNotaForm] = useState({ titulo: "", conteudo: "", cor: "yellow" });
  const [savingNota, setSavingNota] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchNotas();
    fetchVerificacoesPendentes();
    fetchEnviosPendentes();
    fetchRotinasPendentes();
    fetchTodosEnvios();
    fetchTiposVerificacao();
    
    // Iniciar tour automaticamente para novos usuários
    const tourSeen = localStorage.getItem('onboarding_completed');
    if (!tourSeen) {
      // Pequeno delay para garantir que os elementos estejam renderizados
      setTimeout(() => {
        setRunTour(true);
      }, 1000);
    }
    
    // Atualizar verificações a cada minuto
    const interval = setInterval(() => {
      fetchVerificacoesPendentes();
      fetchEnviosPendentes();
      fetchRotinasPendentes();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const data = await apiRequest("/dashboard");
      setStats(data);
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificacoesPendentes = async () => {
    try {
      const data = await apiRequest("/itens-verificacoes-pendentes");
      setVerificacoesPendentes(data);
    } catch (error) {
      console.error("Erro ao carregar verificações pendentes:", error);
    }
  };

  const fetchEnviosPendentes = async () => {
    try {
      const data = await apiRequest("/envios-pendentes");
      setEnviosPendentes(data);
    } catch (error) {
      console.error("Erro ao carregar envios pendentes:", error);
    }
  };

  const fetchTodosEnvios = async () => {
    setLoadingEnvios(true);
    try {
      const data = await apiRequest("/envios");
      setTodosEnvios(data);
    } catch (error) {
      console.error("Erro ao carregar envios:", error);
    } finally {
      setLoadingEnvios(false);
    }
  };

  const fetchRotinasPendentes = async () => {
    try {
      const data = await apiRequest("/rotinas-pendentes");
      setRotinasPendentes(data);
    } catch (error) {
      console.error("Erro ao carregar rotinas pendentes:", error);
    }
  };

  const fetchTiposVerificacao = async () => {
    try {
      const data = await apiRequest("/verificacoes/tipos");
      setTiposVerificacao(data);
    } catch (error) {
      console.error("Erro ao carregar tipos de verificação:", error);
    }
  };

  const handleAbrirRotinaDialog = (rotina) => {
    // Buscar os campos do tipo de verificação
    const tipo = tiposVerificacao.find(t => t.id === rotina.tipo_id);
    setRotinaSelecionada({
      ...rotina,
      campos: tipo?.campos || [],
      cor: tipo?.cor || "#6366f1"
    });
    setRotinaFormData({
      conforme: true,
      responsavel: "",
      observacoes: "",
      campos_valores: {}
    });
    setShowRotinaDialog(true);
  };

  const handleRegistrarRotina = async () => {
    if (!rotinaSelecionada) return;
    
    setRegistrandoRotina(rotinaSelecionada.tipo_id);
    try {
      await apiRequest(`/verificacoes/custom`, { 
        method: "POST",
        body: JSON.stringify({
          tipo_verificacao_id: rotinaSelecionada.tipo_id,
          data_verificacao: hojeBrasil(),
          hora_verificacao: horaBrasil(),
          conforme: rotinaFormData.conforme,
          responsavel: rotinaFormData.responsavel || "Registro via Dashboard",
          observacoes: rotinaFormData.observacoes,
          campos_valores: rotinaFormData.campos_valores
        })
      });
      toast.success("Rotina registrada com sucesso!");
      setShowRotinaDialog(false);
      fetchRotinasPendentes();
    } catch (error) {
      toast.error(error.message || "Erro ao registrar rotina");
    } finally {
      setRegistrandoRotina(null);
    }
  };

  const handleMarcarEnviado = (envio) => {
    // Abrir diálogo para marcar envio com observações e evidência
    setEnvioSelecionado(envio);
    setMarcarEnvioDialogOpen(true);
  };

  const handleEnvioMarcadoSuccess = () => {
    fetchEnviosPendentes();
    fetchTodosEnvios();
  };

  // Função para abrir o modal correto baseado no tipo de verificação
  const abrirModalVerificacao = (item) => {
    setItemParaVerificar(item);
    const dataAtual = hojeBrasil();
    const horaAtual = horaBrasil();
    
    if (item.tipo_verificacao === "balanca") {
      setBalancaForm({
        data_verificacao: dataAtual,
        hora_verificacao: horaAtual,
        peso_padrao: "",
        peso_indicado: "",
        diferenca: "",
        conforme: true,
        responsavel: "",
        observacoes: "",
      });
      setBalancaModalOpen(true);
    } else if (item.tipo_verificacao === "termohigrometro") {
      setTermoForm({
        data_verificacao: dataAtual,
        hora_verificacao: horaAtual,
        temperatura_lida: "",
        umidade_lida: "",
        conforme: true,
        responsavel: "",
        observacoes: "",
      });
      setTermoModalOpen(true);
    } else {
      // Custom ou não identificado
      setCustomForm({
        data_verificacao: dataAtual,
        hora_verificacao: horaAtual,
        conforme: true,
        responsavel: "",
        observacoes: "",
        arquivo_url: "",
        arquivo_nome: "",
      });
      setCustomModalOpen(true);
    }
  };

  // Handler para calcular diferença na balança
  const handleBalancaChange = (field, value) => {
    const newForm = { ...balancaForm, [field]: value };
    if (field === "peso_padrao" || field === "peso_indicado") {
      const padrao = parseFloat(newForm.peso_padrao) || 0;
      const indicado = parseFloat(newForm.peso_indicado) || 0;
      if (padrao > 0 && indicado > 0) {
        newForm.diferenca = (indicado - padrao).toFixed(4);
      }
    }
    setBalancaForm(newForm);
  };

  // Salvar verificação de balança
  const handleSaveBalanca = async () => {
    if (!itemParaVerificar) return;
    if (!balancaForm.peso_padrao || !balancaForm.peso_indicado || !balancaForm.responsavel) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    
    setSaving(true);
    try {
      await apiRequest("/verificacoes/balanca", {
        method: "POST",
        body: JSON.stringify({
          item_id: itemParaVerificar.item_id,
          ...balancaForm,
          peso_padrao: parseFloat(balancaForm.peso_padrao),
          peso_indicado: parseFloat(balancaForm.peso_indicado),
          diferenca: parseFloat(balancaForm.diferenca),
        }),
      });
      
      toast.success("Verificação de balança registrada!");
      setBalancaModalOpen(false);
      setItemParaVerificar(null);
      fetchVerificacoesPendentes();
    } catch (error) {
      toast.error(error.message || "Erro ao salvar verificação");
    } finally {
      setSaving(false);
    }
  };

  // Salvar verificação de termohigrômetro
  const handleSaveTermo = async () => {
    if (!itemParaVerificar) return;
    if (!termoForm.temperatura_lida || !termoForm.temperatura_min || !termoForm.temperatura_max || !termoForm.umidade_lida || !termoForm.responsavel) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    if (parseFloat(termoForm.temperatura_min) > parseFloat(termoForm.temperatura_max)) {
      toast.error("Temperatura mínima não pode ser maior que a máxima");
      return;
    }
    
    setSaving(true);
    try {
      const resp = await apiRequest("/verificacoes/termohigrometro", {
        method: "POST",
        body: JSON.stringify({
          item_id: itemParaVerificar.item_id,
          ...termoForm,
          temperatura_min: parseFloat(termoForm.temperatura_min),
          temperatura_max: parseFloat(termoForm.temperatura_max),
          temperatura_lida: parseFloat(termoForm.temperatura_lida),
          umidade_lida: parseFloat(termoForm.umidade_lida),
        }),
      });
      
      if (resp?.fora_faixa?.length > 0) {
        toast.warning("Registrado como NÃO CONFORME: leitura fora da faixa permitida");
      } else {
        toast.success("Verificação de termohigrômetro registrada!");
      }
      setTermoModalOpen(false);
      setItemParaVerificar(null);
      fetchVerificacoesPendentes();
    } catch (error) {
      toast.error(error.message || "Erro ao salvar verificação");
    } finally {
      setSaving(false);
    }
  };

  // Upload de arquivo para verificação custom
  const handleCustomFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx', '.xls', '.xlsx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error("Formato não permitido. Use: PDF, PNG, JPG, DOC, DOCX, XLS, XLSX");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 10MB");
      return;
    }
    
    setCustomUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: formData,
      });
      
      if (!response.ok) throw new Error("Erro ao fazer upload");
      const data = await response.json();
      
      setCustomForm({ ...customForm, arquivo_url: data.url, arquivo_nome: file.name });
      toast.success("Arquivo anexado!");
    } catch (error) {
      toast.error("Erro ao fazer upload do arquivo");
    } finally {
      setCustomUploading(false);
    }
  };

  // Salvar verificação custom
  const handleSaveCustom = async () => {
    if (!itemParaVerificar) return;
    if (!customForm.responsavel) {
      toast.error("Preencha o nome do responsável");
      return;
    }
    
    setSaving(true);
    try {
      // Registra no endpoint de verificação diária
      await apiRequest(`/itens/${itemParaVerificar.item_id}/verificar`, {
        method: "POST",
        body: JSON.stringify({
          valor_medido: customForm.conforme ? "Conforme" : "Não conforme",
          observacao: `Responsável: ${customForm.responsavel}. ${customForm.observacoes || ""}`
        })
      });
      
      toast.success("Verificação registrada!");
      setCustomModalOpen(false);
      setItemParaVerificar(null);
      fetchVerificacoesPendentes();
    } catch (error) {
      toast.error(error.message || "Erro ao salvar verificação");
    } finally {
      setSaving(false);
    }
  };

  const fetchNotas = async () => {
    try {
      const data = await apiRequest("/notas");
      setNotas(data);
    } catch (error) {
      console.error("Erro ao carregar notas:", error);
    }
  };

  const handleOpenNotaDialog = (nota = null) => {
    if (nota) {
      setEditingNota(nota);
      setNotaForm({ titulo: nota.titulo, conteudo: nota.conteudo, cor: nota.cor });
    } else {
      setEditingNota(null);
      setNotaForm({ titulo: "", conteudo: "", cor: "yellow" });
    }
    setNotaDialogOpen(true);
  };

  const handleSaveNota = async () => {
    if (!notaForm.titulo.trim() || !notaForm.conteudo.trim()) {
      toast.error("Preencha o título e conteúdo da nota");
      return;
    }
    
    setSavingNota(true);
    try {
      if (editingNota) {
        await apiRequest(`/notas/${editingNota.id}`, {
          method: "PUT",
          body: JSON.stringify(notaForm)
        });
        toast.success("Nota atualizada!");
      } else {
        await apiRequest("/notas", {
          method: "POST",
          body: JSON.stringify(notaForm)
        });
        toast.success("Nota criada!");
      }
      await fetchNotas();
      setNotaDialogOpen(false);
      setNotaForm({ titulo: "", conteudo: "", cor: "yellow" });
      setEditingNota(null);
    } catch (error) {
      console.error("Erro ao salvar nota:", error);
      toast.error(error.message || "Erro ao salvar nota. Tente novamente.");
    } finally {
      setSavingNota(false);
    }
  };

  const handleDeleteNotaClick = (notaId) => {
    setNotaToDelete(notaId);
    setShowPasswordDialog(true);
  };

  const handleDeleteNotaConfirm = async () => {
    if (!notaToDelete) return;
    try {
      await apiRequest(`/notas/${notaToDelete}`, { method: "DELETE" });
      toast.success("Nota excluída!");
      fetchNotas();
    } catch (error) {
      toast.error("Erro ao excluir nota");
    } finally {
      setNotaToDelete(null);
    }
  };

  const handleToggleFixar = async (nota) => {
    try {
      await apiRequest(`/notas/${nota.id}`, {
        method: "PUT",
        body: JSON.stringify({ fixada: !nota.fixada })
      });
      fetchNotas();
    } catch (error) {
      toast.error("Erro ao fixar nota");
    }
  };

  const corClasses = {
    yellow: "bg-yellow-100 border-yellow-300",
    blue: "bg-blue-100 border-blue-300",
    green: "bg-green-100 border-green-300",
    pink: "bg-pink-100 border-pink-300",
    purple: "bg-purple-100 border-purple-300",
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Onboarding Tour */}
      <OnboardingTour 
        steps={dashboardTourSteps}
        run={runTour}
        onComplete={completeTour}
        onSkip={() => setRunTour(false)}
        onNeverShowAgain={completeTour}
      />
      
      <div className="space-y-6 animate-slide-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
              Dashboard
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Visão geral dos eventos sanitários</p>
          </div>
          
          {/* Botão para reiniciar tour */}
          {tourCompleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                resetTour();
                setTimeout(() => setRunTour(true), 100);
              }}
              className="flex items-center gap-2"
              data-testid="restart-tour-btn"
            >
              <HelpCircle className="w-4 h-4" />
              Ver Tour Novamente
            </Button>
          )}
        </div>

        {/* Tabs de navegação */}
        <Tabs defaultValue="visao-geral" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="visao-geral" data-testid="tab-visao-geral" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span>Visão Geral</span>
            </TabsTrigger>
            <TabsTrigger value="envios" data-testid="tab-envios" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              <span>Envios</span>
              {enviosPendentes.filter(e => e.atrasado).length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {enviosPendentes.filter(e => e.atrasado).length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Aba Visão Geral */}
          <TabsContent value="visao-geral" className="space-y-6 mt-6">

        {/* Painel de Status dos Eventos */}
        <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-gray-50">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Status dos Eventos
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
              {/* Em Dia - Verde */}
              <div className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border text-center sm:text-left ${stats?.em_dia > 0 ? 'bg-emerald-100 border-emerald-300' : 'bg-gray-50 border-gray-200'}`} data-testid="status-em-dia">
                <CheckCircle2 className={`w-6 h-6 sm:w-8 sm:h-8 ${stats?.em_dia > 0 ? 'text-emerald-600' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-xl sm:text-2xl font-bold ${stats?.em_dia > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>{stats?.em_dia || 0}</p>
                  <p className={`text-xs font-medium ${stats?.em_dia > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>EM DIA</p>
                </div>
              </div>
              
              {/* Em 60 dias - Amarelo */}
              <div className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border text-center sm:text-left ${stats?.alerta_60_dias > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`} data-testid="status-60-dias">
                <Clock className={`w-6 h-6 sm:w-8 sm:h-8 ${stats?.alerta_60_dias > 0 ? 'text-amber-500' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-xl sm:text-2xl font-bold ${stats?.alerta_60_dias > 0 ? 'text-amber-600' : 'text-gray-500'}`}>{stats?.alerta_60_dias || 0}</p>
                  <p className={`text-xs font-medium ${stats?.alerta_60_dias > 0 ? 'text-amber-500' : 'text-gray-400'}`}>60 dias</p>
                </div>
              </div>
              
              {/* Em 30 dias - Laranja */}
              <div className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border text-center sm:text-left ${stats?.alerta_30_dias > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`} data-testid="status-30-dias">
                <AlertTriangle className={`w-6 h-6 sm:w-8 sm:h-8 ${stats?.alerta_30_dias > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-xl sm:text-2xl font-bold ${stats?.alerta_30_dias > 0 ? 'text-orange-600' : 'text-gray-500'}`}>{stats?.alerta_30_dias || 0}</p>
                  <p className={`text-xs font-medium ${stats?.alerta_30_dias > 0 ? 'text-orange-500' : 'text-gray-400'}`}>30 dias</p>
                </div>
              </div>
              
              {/* Em 7 dias - Vermelho claro */}
              <div className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border text-center sm:text-left ${stats?.alerta_7_dias > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`} data-testid="status-7-dias">
                <AlertCircle className={`w-6 h-6 sm:w-8 sm:h-8 ${stats?.alerta_7_dias > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-xl sm:text-2xl font-bold ${stats?.alerta_7_dias > 0 ? 'text-red-600' : 'text-gray-500'}`}>{stats?.alerta_7_dias || 0}</p>
                  <p className={`text-xs font-medium ${stats?.alerta_7_dias > 0 ? 'text-red-500' : 'text-gray-400'}`}>7 dias</p>
                </div>
              </div>
              
              {/* Vencidos - Vermelho escuro */}
              <div className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border text-center sm:text-left col-span-2 sm:col-span-1 ${stats?.vencidos > 0 ? 'bg-red-100 border-red-300' : 'bg-gray-50 border-gray-200'}`} data-testid="status-vencidos">
                <XCircle className={`w-6 h-6 sm:w-8 sm:h-8 ${stats?.vencidos > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                <div>
                  <p className={`text-xl sm:text-2xl font-bold ${stats?.vencidos > 0 ? 'text-red-700' : 'text-gray-500'}`}>{stats?.vencidos || 0}</p>
                  <p className={`text-xs font-medium ${stats?.vencidos > 0 ? 'text-red-600' : 'text-gray-400'}`}>VENCIDOS</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verificações de Itens Pendentes */}
        {verificacoesPendentes.length > 0 && (
          <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-md" data-testid="verificacoes-pendentes-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-amber-100 rounded-full animate-pulse">
                    <Bell className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-amber-900" style={{ fontFamily: 'Manrope' }}>
                      Verificações de Itens Pendentes
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      {verificacoesPendentes.filter(v => v.atrasado).length > 0 
                        ? `${verificacoesPendentes.filter(v => v.atrasado).length} verificação(ões) atrasada(s)!`
                        : "Verificações programadas para hoje"
                      }
                    </CardDescription>
                  </div>
                </div>
                <span className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm font-bold">
                  {verificacoesPendentes.length}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {verificacoesPendentes.map((v, idx) => {
                  // Escolher ícone e cor baseado no tipo
                  let IconComponent = ClipboardCheck;
                  let iconColor = "text-purple-600";
                  let iconBg = "bg-purple-100";
                  let btnColor = "bg-purple-600 hover:bg-purple-700";
                  
                  if (v.tipo_verificacao === "balanca") {
                    IconComponent = Scale;
                    iconColor = "text-teal-600";
                    iconBg = "bg-teal-100";
                    btnColor = "bg-teal-600 hover:bg-teal-700";
                  } else if (v.tipo_verificacao === "termohigrometro") {
                    IconComponent = Thermometer;
                    iconColor = "text-amber-600";
                    iconBg = "bg-amber-100";
                    btnColor = "bg-amber-600 hover:bg-amber-700";
                  }
                  
                  // Override para atrasados
                  if (v.atrasado) {
                    iconBg = "bg-red-100";
                    iconColor = "text-red-600";
                    btnColor = "bg-red-600 hover:bg-red-700";
                  }
                  
                  return (
                    <div 
                      key={`${v.item_id}-${v.horario}-${idx}`}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        v.atrasado 
                          ? 'bg-red-50 border-red-200' 
                          : 'bg-white border-amber-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${iconBg}`}>
                          <IconComponent className={`w-4 h-4 ${iconColor}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{v.item_nome}</p>
                          <p className="text-sm text-slate-500">
                            {v.setor} • Horário: {v.horario}
                            {v.atrasado && <span className="text-red-600 font-medium ml-2">ATRASADO</span>}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => abrirModalVerificacao(v)}
                        disabled={saving}
                        className={btnColor}
                        data-testid={`verificar-item-${v.item_id}`}
                      >
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          Verificar
                        </span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Envios Mensais Pendentes */}
        {enviosPendentes.length > 0 && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Send className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-blue-900" style={{ fontFamily: 'Manrope' }}>
                      Envios Mensais Pendentes
                    </CardTitle>
                    <CardDescription className="text-blue-600">
                      {enviosPendentes.filter(e => e.atrasado).length > 0 
                        ? `${enviosPendentes.filter(e => e.atrasado).length} envio(s) atrasado(s)!`
                        : "Envios programados para este período"}
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-blue-600 text-white">
                  {enviosPendentes.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {enviosPendentes.map((envio) => (
                  <div 
                    key={envio.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      envio.atrasado 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-white border-blue-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${envio.atrasado ? 'bg-red-100' : 'bg-blue-100'}`}>
                        <Send className={`w-4 h-4 ${envio.atrasado ? 'text-red-600' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{envio.nome}</p>
                        <p className="text-sm text-slate-500">
                          Dia {envio.dia_envio} de cada mês
                          {envio.atrasado && <span className="text-red-600 font-medium ml-2">ATRASADO</span>}
                          {!envio.atrasado && envio.dias_restantes <= 3 && (
                            <span className="text-amber-600 font-medium ml-2">Faltam {envio.dias_restantes} dias</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleMarcarEnviado(envio)}
                      className={envio.atrasado 
                        ? "bg-red-600 hover:bg-red-700" 
                        : "bg-blue-600 hover:bg-blue-700"
                      }
                    >
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Marcar Enviado
                      </span>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rotinas Personalizadas Pendentes */}
        {rotinasPendentes.length > 0 && (
          <Card className="border-purple-200 bg-purple-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <ClipboardCheck className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-purple-900" style={{ fontFamily: 'Manrope' }}>
                      Rotinas Pendentes Hoje
                    </CardTitle>
                    <CardDescription className="text-purple-600">
                      Verificações personalizadas que precisam ser realizadas
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-purple-600 text-white">
                  {rotinasPendentes.reduce((acc, r) => acc + r.pendente, 0)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {rotinasPendentes.map((rotina) => (
                  <div 
                    key={rotina.tipo_id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-white border-purple-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-purple-100">
                        <ClipboardCheck className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{rotina.nome}</p>
                        <p className="text-sm text-slate-500">
                          {rotina.periodicidade_label && (
                            <span className="text-slate-400 mr-2" data-testid={`rotina-pendente-label-${rotina.tipo_id}`}>{rotina.periodicidade_label} •</span>
                          )}
                          {rotina.qtd_feita}/{rotina.qtd_esperada} realizadas
                          {rotina.pendente > 1 && (
                            <span className="text-purple-600 font-medium ml-2">
                              Faltam {rotina.pendente}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAbrirRotinaDialog(rotina)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Registrar</span>
                      </span>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total de Eventos"
            value={stats?.total_eventos || 0}
            icon={TrendingUp}
            color="teal"
            description="Eventos ativos"
          />
          <StatCard
            title="Em Dia"
            value={stats?.em_dia || 0}
            icon={CheckCircle2}
            color="emerald"
            description="Tudo certo"
          />
          <StatCard
            title="Vencendo"
            value={stats?.vencendo || 0}
            icon={AlertTriangle}
            color="amber"
            description="Próximos 30 dias"
          />
          <StatCard
            title="Vencidos"
            value={stats?.vencidos || 0}
            icon={XCircle}
            color="red"
            description="Ação necessária"
          />
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Próximos Vencimentos */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  Próximos Vencimentos
                </CardTitle>
                <CardDescription>Eventos que vencem nos próximos 60 dias</CardDescription>
              </div>
              <Bell className="w-5 h-5 text-slate-400" />
            </CardHeader>
            <CardContent>
              {stats?.proximos_vencimentos?.length > 0 ? (
                <div className="space-y-1">
                  {stats.proximos_vencimentos.map((evento) => (
                    <UpcomingEventRow key={evento.id} evento={evento} />
                  ))}
                  <Button variant="ghost" asChild className="w-full mt-4">
                    <Link to="/eventos" className="text-teal-600">
                      Ver todos os eventos
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                  <p className="font-medium">Nenhum evento vencendo</p>
                  <p className="text-sm">Todos os eventos estão em dia!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Eventos por Tipo */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Por Categoria
              </CardTitle>
              <CardDescription>Distribuição de eventos</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.eventos_por_tipo && Object.keys(stats.eventos_por_tipo).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.eventos_por_tipo).map(([tipo, count]) => (
                    <div key={tipo} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 truncate flex-1">
                        {getTipoEventoLabel(tipo)}
                      </span>
                      <span className="text-sm font-semibold text-slate-900 ml-2">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CalendarDays className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm">Nenhum evento cadastrado</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alerta de Eventos Vencidos */}
        {stats?.vencidos > 0 && (
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900">Atenção: Eventos Vencidos</h3>
                  <p className="text-sm text-red-700">
                    Você tem {stats.vencidos} evento(s) vencido(s) que requerem ação imediata.
                  </p>
                </div>
                <Button asChild variant="destructive">
                  <Link to="/eventos?status=vencido">
                    Ver eventos
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bloco de Notas */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <StickyNote className="w-5 h-5 text-yellow-500" />
                Bloco de Notas
              </CardTitle>
              <CardDescription>Anotações e lembretes rápidos</CardDescription>
            </div>
            <Button size="sm" onClick={() => handleOpenNotaDialog()}>
              <Plus className="w-4 h-4 mr-1" />
              Nova Nota
            </Button>
          </CardHeader>
          <CardContent>
            {notas.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {notas.map((nota) => (
                  <div 
                    key={nota.id} 
                    className={`relative p-4 rounded-lg border-2 ${corClasses[nota.cor] || corClasses.yellow} transition-shadow hover:shadow-md`}
                  >
                    {nota.fixada && (
                      <Pin className="absolute top-2 right-2 w-4 h-4 text-slate-600" />
                    )}
                    <h4 className="font-semibold text-slate-800 mb-2 pr-6 truncate">{nota.titulo}</h4>
                    <p className="text-sm text-slate-600 line-clamp-3 whitespace-pre-wrap">{nota.conteudo}</p>
                    <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-slate-200/50">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => handleToggleFixar(nota)}
                        title={nota.fixada ? "Desafixar" : "Fixar"}
                      >
                        <Pin className={`w-3.5 h-3.5 ${nota.fixada ? "text-teal-600" : "text-slate-400"}`} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => handleOpenNotaDialog(nota)}
                      >
                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => handleDeleteNotaClick(nota.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <StickyNote className="w-12 h-12 mx-auto text-yellow-300 mb-3" />
                <p className="font-medium">Nenhuma nota</p>
                <p className="text-sm">Crie notas para lembretes rápidos!</p>
              </div>
            )}
          </CardContent>
        </Card>

          </TabsContent>

          {/* Aba de Envios */}
          <TabsContent value="envios" className="space-y-6 mt-6">
            {/* Header da aba de envios */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Controle de Envios
                </h2>
                <p className="text-slate-500 text-sm">
                  Acompanhe todos os envios mensais obrigatórios
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchTodosEnvios} disabled={loadingEnvios}>
                  {loadingEnvios ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span className="ml-2 hidden sm:inline">Atualizar</span>
                </Button>
                <Button asChild size="sm" className="bg-teal-600 hover:bg-teal-700">
                  <Link to="/envios">
                    <Plus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Novo Envio</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Cards de estatísticas de envios */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Send className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-700">{todosEnvios.length}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-700">
                        {todosEnvios.filter(e => e.ultimo_envio).length}
                      </p>
                      <p className="text-xs text-emerald-600">Enviados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700">
                        {enviosPendentes.filter(e => !e.atrasado).length}
                      </p>
                      <p className="text-xs text-amber-600">Pendentes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">
                        {enviosPendentes.filter(e => e.atrasado).length}
                      </p>
                      <p className="text-xs text-red-600">Atrasados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lista de todos os envios */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  Todos os Envios Cadastrados
                </CardTitle>
                <CardDescription>
                  Lista completa dos envios mensais obrigatórios
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingEnvios ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : todosEnvios.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Send className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="font-medium">Nenhum envio cadastrado</p>
                    <p className="text-sm mb-4">Cadastre seus envios mensais obrigatórios</p>
                    <Button asChild className="bg-teal-600 hover:bg-teal-700">
                      <Link to="/envios">
                        <Plus className="w-4 h-4 mr-2" />
                        Cadastrar Envio
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {todosEnvios.map((envio) => {
                      // Usar apenas a lógica do backend (enviosPendentes)
                      // O backend já considera se o envio foi criado este mês após o dia de envio
                      const pendente = enviosPendentes.find(e => e.id === envio.id);
                      const statusAtrasado = pendente?.atrasado || false;
                      const statusPendente = pendente && !pendente.atrasado;
                      
                      return (
                        <div 
                          key={envio.id}
                          className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border ${
                            statusAtrasado 
                              ? 'border-red-200 bg-red-50/50' 
                              : statusPendente 
                                ? 'border-amber-200 bg-amber-50/50'
                                : 'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start sm:items-center gap-3 mb-3 sm:mb-0">
                            <div className={`p-2 rounded-lg ${
                              statusAtrasado 
                                ? 'bg-red-100' 
                                : statusPendente 
                                  ? 'bg-amber-100'
                                  : 'bg-teal-100'
                            }`}>
                              <Send className={`w-5 h-5 ${
                                statusAtrasado 
                                  ? 'text-red-600' 
                                  : statusPendente 
                                    ? 'text-amber-600'
                                    : 'text-teal-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{envio.nome}</p>
                              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Dia {envio.dia_envio}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {envio.recorrencia?.charAt(0).toUpperCase() + envio.recorrencia?.slice(1) || 'Mensal'}
                                </Badge>
                                {statusAtrasado && (
                                  <Badge variant="destructive" className="text-xs">
                                    Atrasado
                                  </Badge>
                                )}
                                {statusPendente && (
                                  <Badge className="bg-amber-500 text-xs">
                                    Pendente
                                  </Badge>
                                )}
                              </div>
                              {envio.ultimo_envio && (
                                <p className="text-xs text-slate-400 mt-1">
                                  Último: {new Date(envio.ultimo_envio).toLocaleDateString('pt-BR')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(statusAtrasado || statusPendente) && (
                              <Button 
                                size="sm"
                                onClick={() => handleMarcarEnviado(envio)}
                                className={statusAtrasado ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Marcar Enviado</span>
                                <span className="sm:hidden">Enviado</span>
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <Link to={`/envios`}>
                                <Pencil className="w-4 h-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog para marcar envio */}
        <MarcarEnvioDialog
          open={marcarEnvioDialogOpen}
          onOpenChange={setMarcarEnvioDialogOpen}
          envio={envioSelecionado}
          onSuccess={handleEnvioMarcadoSuccess}
        />

        {/* Dialog para criar/editar nota */}
        <Dialog open={notaDialogOpen} onOpenChange={setNotaDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingNota ? "Editar Nota" : "Nova Nota"}</DialogTitle>
              <DialogDescription>
                {editingNota ? "Atualize sua anotação" : "Crie uma anotação rápida"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Título</label>
                <Input
                  value={notaForm.titulo}
                  onChange={(e) => setNotaForm({ ...notaForm, titulo: e.target.value })}
                  placeholder="Título da nota"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Conteúdo</label>
                <Textarea
                  value={notaForm.conteudo}
                  onChange={(e) => setNotaForm({ ...notaForm, conteudo: e.target.value })}
                  placeholder="Escreva sua anotação..."
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Cor</label>
                <div className="flex gap-2">
                  {["yellow", "blue", "green", "pink", "purple"].map((cor) => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setNotaForm({ ...notaForm, cor })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        corClasses[cor]
                      } ${notaForm.cor === cor ? "ring-2 ring-offset-2 ring-slate-400" : ""}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotaDialogOpen(false)} disabled={savingNota}>
                Cancelar
              </Button>
              <Button onClick={handleSaveNota} disabled={savingNota} className="min-w-[100px]">
                {savingNota ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Verificação de Balança */}
        <Dialog open={balancaModalOpen} onOpenChange={setBalancaModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Scale className="w-5 h-5 text-teal-600" />
                Verificação de Balança
              </DialogTitle>
              <DialogDescription>
                {itemParaVerificar && (
                  <>Registre a verificação diária com peso padrão - <strong>{itemParaVerificar.item_nome}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input type="date" value={balancaForm.data_verificacao} onChange={(e) => handleBalancaChange("data_verificacao", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Hora *</Label>
                  <Input type="time" value={balancaForm.hora_verificacao} onChange={(e) => handleBalancaChange("hora_verificacao", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Peso Padrão (g) *</Label>
                  <Input type="number" step="0.0001" value={balancaForm.peso_padrao} onChange={(e) => handleBalancaChange("peso_padrao", e.target.value)} placeholder="100.0000" />
                </div>
                <div className="space-y-2">
                  <Label>Peso Indicado (g) *</Label>
                  <Input type="number" step="0.0001" value={balancaForm.peso_indicado} onChange={(e) => handleBalancaChange("peso_indicado", e.target.value)} placeholder="100.0001" />
                </div>
                <div className="space-y-2">
                  <Label>Diferença (g)</Label>
                  <Input type="text" value={balancaForm.diferenca} readOnly className="bg-slate-50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Input value={balancaForm.responsavel} onChange={(e) => handleBalancaChange("responsavel", e.target.value)} placeholder="Nome do responsável" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="conforme-balanca" checked={balancaForm.conforme} onCheckedChange={(checked) => handleBalancaChange("conforme", checked)} />
                <Label htmlFor="conforme-balanca">Conforme</Label>
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea value={balancaForm.observacoes} onChange={(e) => handleBalancaChange("observacoes", e.target.value)} placeholder="Observações adicionais..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBalancaModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveBalanca} className="bg-teal-600 hover:bg-teal-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Verificação de Termohigrômetro */}
        <Dialog open={termoModalOpen} onOpenChange={setTermoModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Thermometer className="w-5 h-5 text-amber-600" />
                Verificação de Termohigrômetro
              </DialogTitle>
              <DialogDescription>
                {itemParaVerificar && (
                  <>Registre a leitura de temperatura e umidade - <strong>{itemParaVerificar.item_nome}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input type="date" value={termoForm.data_verificacao} onChange={(e) => setTermoForm({ ...termoForm, data_verificacao: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Hora *</Label>
                  <Input type="time" value={termoForm.hora_verificacao} onChange={(e) => setTermoForm({ ...termoForm, hora_verificacao: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Temp. Mínima (°C) *</Label>
                  <Input type="number" step="0.1" data-testid="dash-termo-temp-min" value={termoForm.temperatura_min || ""} onChange={(e) => setTermoForm({ ...termoForm, temperatura_min: e.target.value })} placeholder="18.0" />
                </div>
                <div className="space-y-2">
                  <Label>Temp. Máxima (°C) *</Label>
                  <Input type="number" step="0.1" data-testid="dash-termo-temp-max" value={termoForm.temperatura_max || ""} onChange={(e) => setTermoForm({ ...termoForm, temperatura_max: e.target.value })} placeholder="25.0" />
                </div>
                <div className="space-y-2">
                  <Label>Temp. Atual (°C) *</Label>
                  <Input type="number" step="0.1" data-testid="dash-termo-temp-atual" value={termoForm.temperatura_lida} onChange={(e) => setTermoForm({ ...termoForm, temperatura_lida: e.target.value })} placeholder="22.5" />
                </div>
                <div className="space-y-2">
                  <Label>Umidade (%) *</Label>
                  <Input type="number" step="0.1" data-testid="dash-termo-umidade" value={termoForm.umidade_lida} onChange={(e) => setTermoForm({ ...termoForm, umidade_lida: e.target.value })} placeholder="55.0" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Input value={termoForm.responsavel} onChange={(e) => setTermoForm({ ...termoForm, responsavel: e.target.value })} placeholder="Nome do responsável" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="conforme-termo" checked={termoForm.conforme} onCheckedChange={(checked) => setTermoForm({ ...termoForm, conforme: checked })} />
                <Label htmlFor="conforme-termo">Conforme</Label>
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea value={termoForm.observacoes} onChange={(e) => setTermoForm({ ...termoForm, observacoes: e.target.value })} placeholder="Observações adicionais..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTermoModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveTermo} className="bg-amber-600 hover:bg-amber-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Verificação Custom (outras atividades) */}
        <Dialog open={customModalOpen} onOpenChange={setCustomModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <ClipboardCheck className="w-5 h-5 text-purple-600" />
                {itemParaVerificar?.item_nome || "Verificação"}
              </DialogTitle>
              <DialogDescription>
                Registre a verificação
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input type="date" value={customForm.data_verificacao} onChange={(e) => setCustomForm({ ...customForm, data_verificacao: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Hora *</Label>
                  <Input type="time" value={customForm.hora_verificacao} onChange={(e) => setCustomForm({ ...customForm, hora_verificacao: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Input value={customForm.responsavel} onChange={(e) => setCustomForm({ ...customForm, responsavel: e.target.value })} placeholder="Nome do responsável" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="conforme-custom" checked={customForm.conforme} onCheckedChange={(checked) => setCustomForm({ ...customForm, conforme: checked })} />
                <Label htmlFor="conforme-custom">Conforme</Label>
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea value={customForm.observacoes} onChange={(e) => setCustomForm({ ...customForm, observacoes: e.target.value })} placeholder="Observações adicionais..." rows={2} />
              </div>
              {/* Upload de Anexo */}
              <div className="space-y-2">
                <Label>Anexo (opcional)</Label>
                <input
                  type="file"
                  ref={customFileInputRef}
                  onChange={handleCustomFileUpload}
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                />
                {customForm.arquivo_url ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span className="flex-1 text-sm truncate">{customForm.arquivo_nome}</span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setCustomForm({ ...customForm, arquivo_url: "", arquivo_nome: "" })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => customFileInputRef.current?.click()}
                    disabled={customUploading}
                  >
                    {customUploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Selecionar arquivo
                  </Button>
                )}
                <p className="text-xs text-slate-500">Formatos: PDF, PNG, JPG, DOC, DOCX, XLS, XLSX (max 10MB)</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCustomModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveCustom} className="bg-purple-600 hover:bg-purple-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Password Dialog para excluir notas */}
        <AdminPasswordDialog
          open={showPasswordDialog}
          onOpenChange={setShowPasswordDialog}
          onConfirm={handleDeleteNotaConfirm}
          title="Excluir Nota"
          description="Esta ação não pode ser desfeita. A nota será removida permanentemente."
        />

        {/* Dialog para registrar rotina personalizada */}
        <Dialog open={showRotinaDialog} onOpenChange={setShowRotinaDialog}>
          <DialogContent className="sm:max-w-md" data-testid="registrar-rotina-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-full"
                  style={{ backgroundColor: `${rotinaSelecionada?.cor || '#6366f1'}20` }}
                >
                  <ClipboardCheck 
                    className="w-6 h-6" 
                    style={{ color: rotinaSelecionada?.cor || '#6366f1' }}
                  />
                </div>
                <div>
                  <DialogTitle className="text-lg">Registrar Rotina</DialogTitle>
                  <DialogDescription>
                    {rotinaSelecionada?.nome}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Info da rotina */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date().toLocaleDateString('pt-BR')}</span>
                  <span>•</span>
                  <Clock className="w-4 h-4" />
                  <span>{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>

              {/* Campos personalizados da rotina */}
              {rotinaSelecionada?.campos && rotinaSelecionada.campos.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">Campos da Rotina</Label>
                  {rotinaSelecionada.campos.map((campo, idx) => (
                    <div key={idx} className="space-y-1">
                      <Label className="text-sm text-slate-600">{campo.nome}</Label>
                      {campo.tipo === "texto" && (
                        <Input
                          placeholder={`Informe ${campo.nome.toLowerCase()}`}
                          value={rotinaFormData.campos_valores[campo.nome] || ""}
                          onChange={(e) => setRotinaFormData(prev => ({
                            ...prev,
                            campos_valores: { ...prev.campos_valores, [campo.nome]: e.target.value }
                          }))}
                        />
                      )}
                      {campo.tipo === "numero" && (
                        <Input
                          type="number"
                          placeholder={`Informe ${campo.nome.toLowerCase()}`}
                          value={rotinaFormData.campos_valores[campo.nome] || ""}
                          onChange={(e) => setRotinaFormData(prev => ({
                            ...prev,
                            campos_valores: { ...prev.campos_valores, [campo.nome]: e.target.value }
                          }))}
                        />
                      )}
                      {campo.tipo === "checkbox" && (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={rotinaFormData.campos_valores[campo.nome] || false}
                            onCheckedChange={(checked) => setRotinaFormData(prev => ({
                              ...prev,
                              campos_valores: { ...prev.campos_valores, [campo.nome]: checked }
                            }))}
                          />
                          <span className="text-sm text-slate-500">
                            {rotinaFormData.campos_valores[campo.nome] ? "Sim" : "Não"}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Responsável */}
              <div className="space-y-2">
                <Label>Responsável</Label>
                <Input
                  placeholder="Nome do responsável"
                  value={rotinaFormData.responsavel}
                  onChange={(e) => setRotinaFormData(prev => ({ ...prev, responsavel: e.target.value }))}
                />
              </div>

              {/* Conforme */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-700">Conforme?</Label>
                  <p className="text-xs text-slate-500">A rotina foi realizada corretamente?</p>
                </div>
                <Switch
                  checked={rotinaFormData.conforme}
                  onCheckedChange={(checked) => setRotinaFormData(prev => ({ ...prev, conforme: checked }))}
                />
              </div>

              {/* Observações */}
              <div className="space-y-2">
                <Label>Observações (opcional)</Label>
                <Textarea
                  placeholder="Alguma observação sobre esta execução..."
                  value={rotinaFormData.observacoes}
                  onChange={(e) => setRotinaFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowRotinaDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleRegistrarRotina}
                disabled={registrandoRotina}
                style={{ backgroundColor: rotinaSelecionada?.cor || '#6366f1' }}
                className="hover:opacity-90"
              >
                {registrandoRotina ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Confirmar Registro
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Dashboard;
